import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  collection,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseDb, getFirebaseFunctions } from './firebase';
import { handleAuthError } from './authRecoveryService';
import type {
  Organization,
  OrganizationMember,
  UserRole,
} from '../types/settings';

// ============================================================================
// ORGANIZATION CRUD
// ============================================================================

/**
 * Create a new organization via Cloud Function
 * Server-side creation prevents the getAfter() vulnerability
 * where clients could inject themselves into arbitrary orgs
 */
export async function createOrganization(
  _userId: string,
  name: string
): Promise<string> {
  const functions = getFirebaseFunctions();
  const createOrgFn = httpsCallable<
    { name: string },
    { success: boolean; orgId: string }
  >(functions, 'createOrganizationFunction');

  const result = await createOrgFn({ name });
  return result.data.orgId;
}

/**
 * Get organization by ID
 */
export async function getOrganization(orgId: string): Promise<Organization | null> {
  const db = getFirebaseDb();
  const orgDoc = await getDoc(doc(db, 'organizations', orgId));

  if (!orgDoc.exists()) {
    return null;
  }

  return orgDoc.data() as Organization;
}

/**
 * Update organization details
 */
export async function updateOrganization(
  orgId: string,
  data: Partial<Pick<Organization, 'name'>>
): Promise<void> {
  const db = getFirebaseDb();
  await updateDoc(doc(db, 'organizations', orgId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Setup organization for a user on first login (auto-creates personal workspace)
 */
export async function setupUserOrganization(
  userId: string,
  displayName?: string
): Promise<string> {
  const db = getFirebaseDb();

  // Check if user already has an organization
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (userDoc.exists()) {
    const userData = userDoc.data();
    if (userData?.organizationId) {
      return userData.organizationId;
    }
  }

  // Create personal workspace
  const workspaceName = displayName
    ? `${displayName}'s Workspace`
    : 'My Workspace';

  return await createOrganization(userId, workspaceName);
}

// ============================================================================
// MEMBER MANAGEMENT
// ============================================================================

/**
 * Get all members of an organization
 */
export async function getOrganizationMembers(
  orgId: string
): Promise<OrganizationMember[]> {
  const db = getFirebaseDb();
  const membersRef = collection(db, 'organizations', orgId, 'members');
  const snapshot = await getDocs(membersRef);

  const members: OrganizationMember[] = [];

  for (const memberDoc of snapshot.docs) {
    const memberData = memberDoc.data();

    // Fetch user details
    const userDoc = await getDoc(doc(db, 'users', memberData.userId));
    const userData = userDoc.exists() ? userDoc.data() : {};

    members.push({
      userId: memberData.userId,
      displayName: userData?.displayName,
      email: userData?.email,
      photoURL: userData?.photoURL,
      role: memberData.role,
      joinedAt: memberData.joinedAt,
      status: memberData.status,
    });
  }

  return members;
}

/**
 * Subscribe to organization members (real-time updates)
 */
export function subscribeOrganizationMembers(
  orgId: string,
  callback: (members: OrganizationMember[]) => void
): () => void {
  const db = getFirebaseDb();
  const membersRef = collection(db, 'organizations', orgId, 'members');

  return onSnapshot(
    membersRef,
    async (snapshot) => {
      const members: OrganizationMember[] = [];

      for (const memberDoc of snapshot.docs) {
        const memberData = memberDoc.data();

        // Fetch user details
        const userDoc = await getDoc(doc(db, 'users', memberData.userId));
        const userData = userDoc.exists() ? userDoc.data() : {};

        members.push({
          userId: memberData.userId,
          displayName: userData?.displayName,
          email: userData?.email,
          photoURL: userData?.photoURL,
          role: memberData.role,
          joinedAt: memberData.joinedAt,
          status: memberData.status,
        });
      }

      callback(members);
    },
    async (error) => {
      console.error('[OrganizationService] Member subscription error:', error);
      await handleAuthError(error);
    }
  );
}

/**
 * Update a member's role via Cloud Function (admin only)
 * Server-side operation prevents unauthorized role changes
 */
export async function updateMemberRole(
  orgId: string,
  userId: string,
  role: UserRole
): Promise<void> {
  const functions = getFirebaseFunctions();
  const updateRoleFn = httpsCallable<
    { orgId: string; userId: string; role: string },
    { success: boolean }
  >(functions, 'updateMemberRoleFunction');

  await updateRoleFn({ orgId, userId, role });
}

/**
 * Remove a member from the organization via Cloud Function (admin only)
 * Server-side operation prevents unauthorized member removal
 */
export async function removeMember(
  orgId: string,
  userId: string
): Promise<void> {
  const functions = getFirebaseFunctions();
  const removeMemberFn = httpsCallable<
    { orgId: string; userId: string },
    { success: boolean }
  >(functions, 'removeMemberFunction');

  await removeMemberFn({ orgId, userId });
}

/**
 * Get organization ID for a user
 */
export async function getUserOrganizationId(userId: string): Promise<string | null> {
  const db = getFirebaseDb();
  const userDoc = await getDoc(doc(db, 'users', userId));

  if (!userDoc.exists()) {
    return null;
  }

  return userDoc.data()?.organizationId || null;
}

